<template>
  <div class="pdv-layout">
    <!-- Topbar compartilhado -->
    <header v-if="mostrarTopbar" class="pdv-layout__topbar">
      <div class="pdv-layout__topbar-left">
        <img
          src="media/logos/logo_nuts_1.png"
          alt="Nuts PDV"
          class="pdv-layout__logo"
        />
        <div v-if="terminalNome" class="pdv-layout__terminal-info">
          <span class="pdv-layout__terminal-name">{{ terminalNome }}</span>
          <span v-if="serie" class="pdv-layout__serie">Série {{ serie }}</span>
        </div>
      </div>

      <div class="pdv-layout__topbar-right">
        <span
          v-if="isDashboard"
          :class="['pdv-layout__status', online ? 'pdv-layout__status--online' : 'pdv-layout__status--offline']"
        >
          <span class="pdv-layout__status-dot"></span>
          {{ online ? "Online" : "Offline" }}
        </span>

        <button
          v-if="isDashboard"
          class="pdv-layout__settings-btn"
          title="Configurações do terminal"
          @click="abrirConfiguracoesDashboard"
        >
          <i class="fas fa-cog"></i>
        </button>

        <span v-if="nomeUsuario" class="pdv-layout__usuario-nome">
          <i class="fas fa-user-circle"></i> {{ nomeUsuario }}
        </span>
        <button
          v-if="!isDashboard"
          class="pdv-layout__back-btn"
          @click="voltarDashboard"
        >
          <i class="fas fa-arrow-left mr-1"></i>
          Dashboard
        </button>
        <button
          class="pdv-layout__logout-btn"
          title="Sair do PDV"
          @click="sair"
        >
          <i class="fas fa-sign-out-alt"></i>
          Sair
        </button>
      </div>
    </header>

    <!-- Área das páginas filhas -->
    <main class="pdv-layout__main">
      <router-view />
    </main>
  </div>
</template>

<script>
import SyncPdvDesktopService from "../services/SyncPdvDesktopService";
import CaixaSyncService from "../services/CaixaSyncService";
import PdvDesktopAuthService from "../services/PdvDesktopAuthService";
import TerminalConfigService from "../services/TerminalConfigService";

export default {
  name: "PdvDesktopLayout",

  data() {
    return {
      online: typeof navigator !== "undefined" ? navigator.onLine : true,
      terminalNome: "",
      serie: "",
      nomeUsuario: "",
    };
  },

  computed: {
    isDashboard() {
      return (
        this.$route.name === "pdvDashboard" ||
        this.$route.name === "pdvDesktopDashboard"
      );
    },
    mostrarTopbar() {
      // A tela de Nova Venda possui header operacional próprio completo.
      return this.$route.name !== "pdvDesktopNovaVenda";
    },
  },

  async mounted() {
    if (!this.$isDesktop) {
      this.$router.push({ name: "pdvDesktopLogin" });
      return;
    }

    window.addEventListener("online", this._updateStatus);
    window.addEventListener("offline", this._updateStatus);

    await this._loadTerminalInfo();

    if (navigator.onLine) {
      this._autoSyncCaixa();
    }
  },

  beforeDestroy() {
    window.removeEventListener("online", this._updateStatus);
    window.removeEventListener("offline", this._updateStatus);
  },

  methods: {
    _updateStatus() {
      const voltouOnline = !this.online && navigator.onLine;
      this.online = navigator.onLine;
      if (voltouOnline) {
        // Primeiro verifica se o terminal ainda está autorizado, depois sincroniza
        this._verificarAutorizacaoTerminal().then((autorizado) => {
          if (autorizado) {
            this._autoSyncCaixa();
            this._autoSyncNfce();
          }
        });
      }
    },

    async _autoSyncCaixa() {
      try {
        await CaixaSyncService.sincronizarCaixasPendentes();
      } catch (e) {
        console.warn("[PdvDesktopLayout] Auto-sync caixa falhou:", e.message);
      }
    },

    /**
     * Consulta o backend para verificar se este dispositivo ainda está
     * vinculado ao terminal. Se não estiver, exibe aviso e redireciona
     * para a tela de configuração do terminal.
     *
     * Retorna true se autorizado (ou em caso de falha de rede), false se
     * o acesso foi bloqueado e o redirecionamento já foi acionado.
     */
    async _verificarAutorizacaoTerminal() {
      try {
        const { autorizado, motivo, code } = await TerminalConfigService.verificarVinculoOnline();
        if (autorizado) return true;

        // Bloqueia operações online imediatamente, antes de mostrar dialog
        TerminalConfigService.marcarDesvinculado();

        // Impede que a mensagem apareça múltiplas vezes
        if (this._bloqueandoTerminal) return false;
        this._bloqueandoTerminal = true;

        await this.$swal.fire({
          icon: "warning",
          title: TerminalConfigService.isCodigoBloqueioEmpresa(code)
            ? "Operacao bloqueada"
            : "Terminal desvinculado",
          text: motivo || (
            TerminalConfigService.isCodigoBloqueioEmpresa(code)
              ? "A empresa esta bloqueada para operacoes no PDV. Entre em contato com o suporte."
              : "Este terminal foi desvinculado no painel web. Configure novamente o terminal para continuar."
          ),
          confirmButtonText: TerminalConfigService.isCodigoBloqueioEmpresa(code)
            ? "Ver bloqueio"
            : "Configurar terminal",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        this._bloqueandoTerminal = false;
        this.$router.push({
          name: TerminalConfigService.isCodigoBloqueioEmpresa(code)
            ? "pdvDesktopBloqueio"
            : "pdvDesktopConfiguracaoTerminal",
        });
        return false;

      } catch (e) {
        console.warn("[PdvDesktopLayout] Erro ao verificar autorização:", e.message);
        return true; // fail-open
      }
    },

    async _autoSyncNfce() {
      try {
        const resultado = await SyncPdvDesktopService.sincronizarNfcesAssinadas();
        if (resultado && resultado.transmitidas > 0) {
          console.info(
            `[PdvDesktopLayout] Auto-sync NFC-e: ${resultado.transmitidas} autorizada(s), ` +
            `${resultado.erros} erro(s).`
          );
        }
      } catch (e) {
        console.warn("[PdvDesktopLayout] Auto-sync NFC-e falhou:", e.message);
      }
    },

    async _loadTerminalInfo() {
      try {
        const api = this.$electron;
        if (!api) return;

        const [info, serie, nomeUsuario] = await Promise.all([
          api.terminal.getInfo(),
          api.config.get("serie_nfe"),
          api.config.get("usuario_nome"),
        ]);

        this.terminalNome = info?.nome || info?.uuid_terminal || "PDV";
        this.serie = serie || "";
        this.nomeUsuario = nomeUsuario || "";
      } catch (e) {
        console.error("[PdvDesktopLayout] Erro ao carregar info do terminal:", e);
      }
    },

    voltarDashboard() {
      this.$router.push({ name: "pdvDesktopDashboard" });
    },

    async sair() {
      try {
        await PdvDesktopAuthService.logout();
      } catch (_) { /* ignore */ }
      this.$router.push({ name: "pdvDesktopLogin" });
    },

    abrirConfiguracoesDashboard() {
      window.dispatchEvent(new CustomEvent("pdv-dashboard-open-settings"));
    },
  },
};
</script>

<style scoped>
.pdv-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  background: #f5f7fa;
}

/* ── Topbar ─────────────────────────────────────────────────────────── */
.pdv-layout__topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 1.5rem;
  background: var(--primary-color, #ee7361);
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.pdv-layout__topbar,
.pdv-layout__topbar * {
  color: #fff;
}

.pdv-layout__topbar-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.pdv-layout__logo {
  max-height: 32px;
  filter: brightness(0) invert(1);
}

.pdv-layout__terminal-info {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.pdv-layout__terminal-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
}

.pdv-layout__serie {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.85);
}

.pdv-layout__topbar-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* Status pill */
.pdv-layout__status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
}

.pdv-layout__status--online {
  background: #16a34a;
  color: #fff;
}

.pdv-layout__status--offline {
  background: #dc2626;
  color: #fff;
}

.pdv-layout__status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

/* Botão voltar */
.pdv-layout__back-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  border-radius: 6px;
  padding: 5px 14px;
  font-size: 0.82rem;
  cursor: pointer;
  transition: background 0.15s;
}

.pdv-layout__back-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Nome do usuário */
.pdv-layout__usuario-nome {
  font-size: 0.78rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* Botão sair */
.pdv-layout__logout-btn {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 0.82rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background 0.15s;
}

.pdv-layout__logout-btn:hover {
  background: rgba(255, 255, 255, 0.22);
}

.pdv-layout__settings-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.pdv-layout__settings-btn:hover {
  background: rgba(255, 255, 255, 0.22);
}

/* ── Main content ────────────────────────────────────────────────────── */
.pdv-layout__main {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
</style>
